import environment from '@square/environment-utils/environment';
import Logger from '@square/dashboard-utilities/utils/logger';
import window from 'ember-window-mock';
const POPUP_WIDTH = 625;
const POPUP_HEIGHT = 325;
const POPUP_BORDERS = 10;
const TITLE_BAR_HEIGHT = 50;
const WINDOW_OPTIONS = {
  status: false,
  resizable: true,
  toolbar: false,
  menubar: false,
  scrollbars: false,
  location: false,
  directories: false,
  height: POPUP_HEIGHT,
  width: POPUP_WIDTH
};
// windowMocks is a mutable object so it can be stubbed in tests
export const windowMocks = {
  isEmberHost: () => {
    return Boolean(window.__isEmberHost);
  },
  isEmberAppReady: () => {
    return Boolean(window.__isEmberAppReady);
  },
  setEmberAppReady: ready => {
    window.__isEmberAppReady = ready;
  },
  ecosystemHeaderInstance: () => {
    return window.__ecosystemHeaderInstance;
  },
  setEcosystemHeaderInstance: instance => {
    window.__ecosystemHeaderInstance = instance;
  },
  ecosystemHeaderActiveApp: () => {
    return window.__ecosystemHeaderActiveApp;
  },
  setEcosystemHeaderActiveApp: activeApp => {
    window.__ecosystemHeaderActiveApp = activeApp;
  },
  dashboardDatadogRum: () => {
    return window.__dashboardDatadogRum;
  },
  reload: () => {
    window.location.reload();
  },
  goBack: () => {
    window.history.go(-1);
  },
  getLocationHref() {
    return window.location.href;
  },
  getLocationSearch() {
    return window.location.search;
  },
  openPopup: (url, name = '', options = {}) => {
    /* eslint-disable no-param-reassign */
    options = {
      ...WINDOW_OPTIONS,
      ...options
    };
    const {
      width,
      height,
      left
    } = options;
    if (typeof left === 'undefined' || left === null) {
      options.left = window.screen.width / 2 - (width || 0 / 2 + POPUP_BORDERS);
    }
    if (typeof options.top === 'undefined' || options.top === null) {
      options.top = window.screen.height / 2 - (height || 0 / 2 + TITLE_BAR_HEIGHT);
    }
    if (typeof options.screenX === 'undefined' || options.screenX === null) {
      options.screenX = left;
    }
    if (typeof options.screenY === 'undefined' || options.screenY === null) {
      options.screenY = options.top;
    }
    let specs = '';
    for (const key in options) {
      const value = options[key]; // eslint-disable-line @typescript-eslint/no-explicit-any
      specs += `${key}=${value},`;
    }
    window.open(url, name, specs);
    /* eslint-enable no-param-reassign */
  },
  getPerformanceObject: () => {
    return window.performance;
  },
  pushState: (data, title, url) => {
    // When testing, we don't want to modify the real URL for the test runner.
    if (environment().isTesting) {
      Logger.log('Manually pushing state onto the window history', data, title, url);
    } else {
      window.history.pushState(data, title, url);
    }
  },
  print: () => {
    window.print();
  },
  navigator: () => {
    return window.navigator;
  },
  getUrlParams(...args) {
    return _getUrlParams(...args);
  },
  getUrlPathname: (location = window.location) => {
    return location.pathname;
  },
  getUrlSearch: (location = window.location) => {
    return location.search;
  },
  getUrlHash: (location = window.location) => {
    return location.hash;
  },
  assign: url => {
    window.location.assign(url);
  },
  open: (...args) => {
    window.open(...args);
  },
  redirect(url) {
    window.location.href = url;
  },
  replace: url => {
    window.location.replace(url);
  },
  getOneTrustActiveGroups: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return window.OnetrustActiveGroups;
  },
  getOneTrustConsentModel: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return window.OneTrust?.GetDomainData?.()?.ConsentModel?.Name;
  },
  getCurrentUserDataPromise: () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return window.currentUserDataPromise;
  },
  setUserData: currentUser => {
    // eslint-disable-next-line dashboard/prefer-user-services
    window.currentUser = currentUser;
  }
};

/**
 * Returns true if the Ember Dashboard application is managing the
 * application shell and should manage global data initialization.
 * Returns false if the Ember application is running within
 * another application host, and should consume global data from that host.
 */
export function isEmberHost() {
  return windowMocks.isEmberHost();
}

/**
 * Returns true if the Ember Dashboard application has fully initialized
 * and is ready to render routes.
 */
export function isEmberAppReady() {
  return windowMocks.isEmberAppReady();
}

/**
 * Sets the Ember app ready state. Called by the ember-app-ready instance
 * initializer after all initializers have completed.
 */
export function setEmberAppReady(ready) {
  windowMocks.setEmberAppReady(ready);
}
export function ecosystemHeaderInstance() {
  return windowMocks.ecosystemHeaderInstance();
}
export function setEcosystemHeaderInstance(instance) {
  windowMocks.setEcosystemHeaderInstance(instance);
}
export function ecosystemHeaderActiveApp() {
  return windowMocks.ecosystemHeaderActiveApp();
}
export function setEcosystemHeaderActiveApp(activeApp) {
  windowMocks.setEcosystemHeaderActiveApp(activeApp);
}
export function dashboardDatadogRum() {
  return windowMocks.dashboardDatadogRum();
}
export function reload() {
  windowMocks.reload();
}
export function goBack() {
  windowMocks.goBack();
}
export function getLocationHref() {
  return windowMocks.getLocationHref();
}
export function getLocationSearch() {
  return windowMocks.getLocationSearch();
}
export function openPopup(url, name = '', options = {}) {
  windowMocks.openPopup(url, name, options);
}
export function getPerformanceObject() {
  return windowMocks.getPerformanceObject();
}
export function pushState(data,
// eslint-disable-line @typescript-eslint/no-explicit-any
title, url) {
  windowMocks.pushState(data, title, url);
}
export function print() {
  windowMocks.print();
}
export function navigator() {
  return windowMocks.navigator();
}

/**
 * Gets a map of URL query string parameter key/value pairs.
 *
 * @param {{ search: string }} location
 * @returns {Map<string, string | boolean | Array<string>>}
 */
function _getUrlParams(location = window.location) {
  const urlParams = new Map();
  const params = location.search.slice(1).split('&');
  const length = params.length;
  for (let i = 0; i < length; i++) {
    if (params[i]) {
      const param = params[i].split('=');
      const name = decodeQueryParam(param[0]);

      // Allow just a key to turn on a flag, e.g., test.html?noglobals
      const value = param.length === 1 || decodeQueryParam(param.slice(1).join('='));
      if (urlParams.has(name)) {
        urlParams.set(name, [].concat(urlParams.get(name), value)); // eslint-disable-line @typescript-eslint/no-explicit-any,unicorn/prefer-spread -- Running into type error with `concat`
      } else {
        urlParams.set(name, value);
      }
    }
  }
  return urlParams;
}
export function getUrlPathname(location = window.location) {
  return windowMocks.getUrlPathname(location);
}
export function getUrlSearch(location = window.location) {
  return windowMocks.getUrlSearch(location);
}
export function getUrlHash(location = window.location) {
  return windowMocks.getUrlHash(location);
}

/**
 * @param {string} param
 * @returns {string}
 */
function decodeQueryParam(param) {
  return decodeURIComponent(param.replace(/\+/g, '%20'));
}

/**
 * Gets the value of a URL flag from the query string. If a truthy/falsy value is used, this function returns a boolean.
 * If not, it returns undefined.
 *
 * @param {string} flag
 * @param {{ search: string }=} location
 * @returns {boolean | undefined}
 */
export function getUrlFlag(flag, location = window.location) {
  const params = getUrlParams(location);
  if (!params.has(flag)) {
    return undefined;
  }
  const value = params.get(flag);
  switch (value) {
    case true:
    case 'true':
    case '1':
      return true;
    case false:
    case 'false':
    case '0':
      return false;
    default:
      return undefined;
  }
}
export function redirect(url) {
  windowMocks.redirect(url);
}
export function assign(url) {
  windowMocks.assign(url);
}
export function open(...args) {
  windowMocks.open(...args);
}
export function replace(url) {
  windowMocks.replace(url);
}
export function getOneTrustActiveGroups() {
  return windowMocks.getOneTrustActiveGroups();
}
export function getOneTrustConsentModel() {
  return windowMocks.getOneTrustConsentModel();
}
export function getCurrentUserDataPromise() {
  return windowMocks.getCurrentUserDataPromise();
}
export function setUserData(user) {
  return windowMocks.setUserData(user);
}
export function getUrlParams(...args) {
  return windowMocks.getUrlParams(...args);
}